import React, { useEffect, useRef, useState } from 'react';

const ServiceCard = () => {
  const cardRef = useRef(null);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1024);

  useEffect(() => {
    // Intersection Observer
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.style.opacity = '1';
            entry.target.style.transform = 'translateY(0)';
          }
        });
      },
      { threshold: 0.1 }
    );

    if (cardRef.current) {
      observer.observe(cardRef.current);
    }

    // Responsive check
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 1024);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      if (cardRef.current) {
        observer.unobserve(cardRef.current);
      }
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const styles = {
    container: {
      width: '100%',
      maxWidth: '90vw',
      margin: '0 auto',
      padding: '0.5vh 1vw',
      textAlign: 'center',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
    },
    heading: {
      color: '#400684',
      fontSize: 'min(7vw, 3.5rem)',
      fontFamily: '"Oswald", sans-serif',
      marginBottom: '1vh',
      fontWeight: 'bold',
      lineHeight: '1.2',
    },
    card: {
      display: 'flex',
      flexDirection: isDesktop ? 'row' : 'column',
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
      boxShadow: '0 2px 10px rgba(0,0,0,0.15)',
      borderRadius: '15px',
      padding: '2vh 3vw',
      margin: '0 auto',
      width: '100%',
      alignItems: 'center',
      justifyContent: 'space-between',
      opacity: '0',
      transform: 'translateY(50px)',
      transition: 'all 0.6s ease-out',
    },
    leftContent: {
      flex: '1',
      textAlign: 'left',
      padding: '1vh 2vw',
      display: 'flex',
      flexDirection: 'column',
      gap: '1vh',
      width: '100%',
    },
    icon: {
      backgroundColor: '#1a1a1a',
      borderRadius: '50%',
      padding: '1.5vh',
      width: 'min(50px, 10vw)',
      height: 'min(50px, 10vw)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    iconImage: {
      width: '60%',
      height: '60%',
      objectFit: 'contain',
    },
    title: {
      color: 'black',
      fontSize: 'min(3vw, 1.5rem)',
      marginBottom: '1vh',
      fontWeight: 'bold',
    },
    description: {
      color: 'black',
      fontSize: 'min(2.5vw, 1rem)',
      marginBottom: '2vh',
      lineHeight: '1.6',
    },
    button: {
      padding: '10px 20px',
      backgroundColor: '#400684',
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      cursor: 'pointer',
      fontWeight: '500',
      transition: 'all 0.2s ease',
      width: 'fit-content',
      fontSize: '14px',
      fontFamily: '"Oswald", sans-serif',
      alignSelf: 'flex-start'
    },
    rightContent: {
      flex: '1',
      position: 'relative',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      maxHeight: '60vh',
    },
    imageContainer: {
      position: 'relative',
      width: '100%',
      height: '100%',
    },
    image: {
      width: '100%',
      height: '100%',
      marginTop: '20px',
      maxWidth: '18vw',
      objectFit: 'cover',
      borderRadius: '10px',
    },
    circle: {
      position: 'absolute',
      bottom: '20px',
      left: '60px',
      width: 'min(80px, 15vw)',
      height: 'min(80px, 15vw)',
      animation: 'rotate 10s linear infinite',
    },
    rotatingText: {
      fill: '#400684',
      fontSize: 'min(8px, 1.5vw)',
      fontWeight: 'bold',
    },
  };

  return (
    <div style={styles.container}>
      <h2 style={styles.heading}>We Have The Best Experts To Elevate Your Business</h2>
      
      <div 
        ref={cardRef} 
        style={{
          ...styles.card,
          flexDirection: isDesktop ? 'row' : 'column'
        }}
      >
        <div style={styles.leftContent}>
          <div style={styles.icon}>
            <img 
              src="/headphone.png" 
              alt="Headphone Icon"
              style={styles.iconImage}
            />
          </div>
          <h3 style={styles.title}>24/7 Call Handling</h3>
          <p style={styles.description}>
            We provide round-the-clock support for all your inbound and outbound call needs, 
            ensuring that no customer query goes unanswered.
          </p>
          <button style={styles.button}>
            Learn More
          </button>
        </div>
        
        {isDesktop && (
          <div style={styles.rightContent}>
            <div style={styles.imageContainer}>
              <img 
                src="/about-img4.jpg" 
                alt="Customer Service Representative" 
                style={styles.image}
              />
              <svg 
                style={{
                  ...styles.circle,
                  transform: 'rotate(0deg)',
                  transformOrigin: 'center',
                  animation: 'rotate 10s linear infinite',
                }}
                viewBox="0 0 100 100"
              >
                <defs>
                  <path 
                    id="circlePath" 
                    d="M 50,50 m -45,0 a 45,45 0 1,1 90,0 a 45,45 0 1,1 -90,0"
                    fill="none"
                    stroke="#400684"
                    strokeWidth="2"
                  />
                </defs>
                <text style={styles.rotatingText}>
                  <textPath href="#circlePath" startOffset="0%">
                    FREE CONSULTATION • FREE CONSULTATION • 
                  </textPath>
                </text>
              </svg>
            </div>
          </div>
        )}
      </div>
      <style>
        {`
          @keyframes rotate {
            from {
              transform: rotate(0deg);
            }
            to {
              transform: rotate(360deg);
            }
          }
        `}
      </style>
    </div>
  );
};

export default ServiceCard;